import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {
  ContactRequirementOverviewEntry,
  ContactRequirementResource,
  CreateContactRequirementRequest,
  UpdateContactRequirementRequest
} from './contact-requirement.models';

/**
 * Talks to the monolith's manager-facing contact-requirement API (`/company/contact-requirement`, #558) —
 * required contact-channel linking, Telegram first. Manager-gated server-side (ShardAdmin / CompanyOwner /
 * PlatformOwner, the same three roles `PermissionService.CanManageAccessGrants()` checks); a 403 surfaces if the
 * calling session holds none of them. Single `BASE_URL` origin, same as every other company http service — the
 * `AuthInterceptor` attaches the context token and the `CM-Secret` cookie rides along.
 */
@Injectable({providedIn: 'root'})
export class ContactRequirementHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get base(): string {
    return `${this.baseUrl}/company/contact-requirement`;
  }

  /** List the company's contact requirements. */
  list(): Observable<ContactRequirementResource[]> {
    return this.http.get<ContactRequirementResource[]>(this.base);
  }

  /** Require a channel of a target (employee, employment-relationship, or every employee). */
  create(request: CreateContactRequirementRequest): Observable<ContactRequirementResource> {
    return this.http.post<ContactRequirementResource>(this.base, request);
  }

  /**
   * Change an existing requirement's gating settings. The channel and target are not editable server-side
   * (re-pointing either would move the effective-from date every deadline hangs off), so this only ever carries
   * the policies, the window and the re-check interval.
   */
  update(id: string, request: UpdateContactRequirementRequest): Observable<ContactRequirementResource> {
    return this.http.put<ContactRequirementResource>(`${this.base}/${id}`, request);
  }

  /** Remove a requirement. */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  /**
   * The per-employee link-state overview for the roster: verdict plus per-channel linked/last-verified/deadline
   * detail. Backs both this screen's overview section and the employee-detail link-state card (filtered
   * client-side to one employee there — there is no per-employee variant of this endpoint).
   */
  overview(): Observable<ContactRequirementOverviewEntry[]> {
    return this.http.get<ContactRequirementOverviewEntry[]>(`${this.base}/overview`);
  }
}
